import { useContext, useEffect, useMemo, useState } from "react";
import Link from "../Link/Link";
import { IDssCardProps } from "./DssCard.interface";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { routes } from "../../shared/router/routes";
import { formatDate, formatTemplate, getFromattedPath } from "../../shared/helpers/utils";
import { DssContext } from "../../shared/providers/dss/dss.provider";
import Button from "../Button/Button";
import { useNavigate } from "react-router-dom";

const DssCard = ({ form, onHome }: IDssCardProps) => {
    const { lang, translate } = useContext(TranslationContext);
    const { resetForm } = useContext(DssContext);
    const navigate = useNavigate();
    const value = useMemo(() => form?.value, [form]);

    const colors = useMemo(() => {        
        if (value === undefined)
            return { bg: "bg-neutrals-white", main: "bg-blue-50", color: "color-blue-500" };
        if (value > 79)
            return { bg: "bg-green-100", main: "bg-green-500", color: "color-neutrals-white" };
        if (value > 59)
            return { bg: "bg-yellow-100", main: "bg-yellow-500", color: "color-neutrals-white" };
        if (value >= 0)
            return { bg: "bg-red-100", main: "bg-red-500", color: "color-neutrals-white" };

        return { bg: "bg-neutrals-white", main: "bg-blue-50", color: "color-blue-500" };
    }, [value]);

    return (
        <article className={`card card_dss p-13 h-100 d-flex flex-column ${colors.bg}`}>
            <div className="d-flex gap-02 gap-md-3 mb-22 justify-content-between">
                <div className="d-flex flex-column gap-02">
                    <span className="text-bd4-bd color-neutrals-dark text-uppercase">
                        {form.last_updated
                            ? formatTemplate(translate("dss.last_updated", "LAST UPDATED {0}"), [
                                  formatDate(form.last_updated, "dd MMMM YYYY", lang)
                              ])
                            : translate("dss.no_assessment_yet", "NO ASSESSMENT DONE YET")}
                    </span>
                    <h3 className="text-h4-md lineclamp-3 lineclamp-md-2 color-neutrals-black">
                        {form.title}
                    </h3>
                </div>
                <span className={`score px-11 py-12 text-h3-smbd ${colors.color} ${colors.main}`}>
                    {value !== undefined ? `${value}%` : "N/A"}
                </span>
            </div>
            {!onHome && (
                <Link
                    to={getFromattedPath(routes.DSS_FORM.path, { formId: form.formId })}
                    variant={value !== undefined ? "outlined" : "solid"}
                    customCss="w-100 mt-auto"
                    label={
                        value
                            ? translate("common.see_details", "See details")
                            : translate("dss.assess_your_workplace", "Assess your workplace")
                    }
                />
            )}
            {onHome && (
                <div className="d-flex gap-03 flex-wrap">
                    {!!value && (
                        <Link
                            to={`${getFromattedPath(routes.DSS_FORM.path, { formId: form.formId })}?reassess=true`}
                            variant={value !== undefined ? "outlined" : "solid"}
                            customCss="d-none d-md-flex flex-grow-1 mt-auto"
                            label={translate("dss.reassess", "Reassess")}
                        />
                    )}
                    <Link
                        to={getFromattedPath(routes.DSS_FORM.path, { formId: form.formId })}
                        variant={value !== undefined ? "outlined" : "solid"}
                        customCss="d-none d-md-flex flex-grow-1 mt-auto"
                        label={
                            value
                                ? translate("dss.assess_your_workplace", "Keep your workplace safe")
                                : translate("dss.reassess", "Assess your workplace")
                        }
                    />
                    <Link
                        to={getFromattedPath(routes.DSS_FORM.path, { formId: form.formId })}
                        variant={value !== undefined ? "outlined" : "solid"}
                        customCss="d-md-none w-100"
                        label={
                            value
                                ? translate("common.see_details", "See details")
                                : translate("dss.assess_your_workplace", "Assess your workplace")
                        }
                    />
                </div>
            )}
        </article>
    );
};

export default DssCard;
